/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import Drawer from '@material-ui/core/Drawer';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import isFunction from 'lodash.isfunction';
import Render from '../../../render/Render';
import {dlsColor, dlsLayout} from '../../../theme/theme';
import {MenuItem, MenuItems} from '../../../types/components';
import {
  FLYOUT_SUBMENU_ITEM_THRESHOLD,
  TOP_NAVIGATION_OFFSET_HEIGHT
} from '../constants';
import NavigationHeader from './NavigationHeader';
import NavigationList from './NavigationList';
import NavigationTopBar from './NavigationTopBar';
import useNavigation from './useNavigation';

const isCurrentPageInMenuItem = (item: MenuItem | undefined, currentPage: string): boolean => (
  !!item?.pages?.some(({page}) => (
    !!page && (page === currentPage || currentPage?.startsWith(`${page}/`))
  ))
);

// the menu item whose pages show as a top navigation bar for this page ...
const findTopNavigationItem = (menuItems: MenuItem[], page: string): MenuItem | undefined => (
  menuItems.find(item => (
    !!item?.pages?.length &&
    item.pages.length >= FLYOUT_SUBMENU_ITEM_THRESHOLD &&
    isCurrentPageInMenuItem(item, page)
  ))
);

/**
 * Defines the props for the current-theme desktop navigation shell.
 */
interface NavigationProps {
  activeIndex?: number;
  collapsed: boolean;
  closeSecondaryNavigation?: () => void;
  currentPage: string;
  drawerPaperClassName: string;
  drawerPaperAnchorLeftClassName: string;
  drawerRootClassName?: string;
  footerContent?: ReactNode | ((collapsed: boolean) => ReactNode);
  footerItems?: MenuItems;
  headerContent?: ReactNode | ((collapsed: boolean) => ReactNode);
  mobileHeaderContent?: ReactNode | ((collapsed: boolean, onClose?: () => void) => ReactNode);
  menuItems: MenuItems;
  onHandleCollapse?: Function;
  onTopNavigationVisibilityChange?: (height: number | null) => void;
  onSetPage(page: string, index: number, isSecondaryIndex?: boolean, item?: MenuItem, isMainMenu?: boolean): void;
  renderedPage?: string;
  selectedIndex: number;
  secondaryMenuItems: MenuItems;
  showSecondaryNavigation?: boolean;
  useExpandCollapseFeature?: boolean;
  mobile?: boolean;
  open?: boolean;
  onClose?: Function;
}

/**
 * Defines the style props used by the current navigation shell.
 */
interface StyleProps {
  collapsed?: boolean;
  mobile?: boolean;
}

const useStyles = makeStyles(() => ({
  drawerPaperAccent : {
    borderRight : `1.5px solid ${dlsColor('pastelTeal')}`
  },
  // mobile sidesheet per Figma: navBarGrey surface, no elevation shadow;
  // `&&&` + !important outranks the wrapper's `&&` drawerPaper background ...
  mobileDrawerPaper : {
    '&&&' : {
      backgroundColor : `${dlsColor('navBarGrey')} !important`,
      boxShadow       : 'none'
    }
  },
  // mobile Figma: the scrim starts below the app header, and the modal root
  // does too so the header (hamburger, notifications) stays tappable; `top`
  // needs !important to beat MUI Modal's inline root style ...
  mobileDrawerRoot : {
    top : `${dlsLayout('header').mobile}px !important`
  },
  mobileBackdrop : {
    backgroundColor : 'rgba(0, 0, 0, 0.3)',
    top             : dlsLayout('header').mobile
  },
  // wraps the header without a box of its own, so its layout is unchanged ...
  headerHoverArea : {
    display : 'contents'
  },
  shell : {
    display        : 'flex',
    flexDirection  : 'column',
    height         : '100%',
    justifyContent : 'space-between',
    overflow       : 'hidden'
  },
  bodyContent : {
    display       : 'flex',
    flex          : 1,
    flexDirection : 'column',
    minHeight     : 0,
    overflow      : 'hidden'
  },
  primarySection : {
    display            : 'flex',
    flex               : 1,
    flexDirection      : 'column',
    minHeight          : 0,
    minWidth           : 0,
    overflowX          : 'hidden',
    overflowY          : 'auto',
    overscrollBehavior : 'contain',
    // the desktop nav's rows run to its footer, per the DLS; the phone
    // sidesheet keeps its 12px ...
    paddingBottom      : ({mobile}: StyleProps) => mobile ? 12 : 0
  },
  footerSection : {
    borderTop  : `1.5px solid ${dlsColor('pastelTeal')}`,
    flexShrink : 0,
    fontFamily : 'Inter',
    overflow   : 'hidden',
    padding    : 0
  }
}));

/**
 * Defines the current-theme desktop navigation shell and flyout behavior.
 *
 * @param props Defines the current navigation content, callbacks, and shell state.
 * @returns The current-theme desktop navigation shell.
 */
const Navigation = ({
  collapsed,
  closeSecondaryNavigation,
  currentPage,
  drawerPaperAnchorLeftClassName,
  drawerPaperClassName,
  drawerRootClassName,
  footerContent,
  headerContent,
  mobileHeaderContent,
  menuItems,
  onHandleCollapse,
  onTopNavigationVisibilityChange,
  onSetPage,
  renderedPage,
  secondaryMenuItems,
  selectedIndex,
  onClose,
  showSecondaryNavigation,
  activeIndex = -1,
  footerItems = [],
  useExpandCollapseFeature = false,
  mobile = false,
  open = true
}: NavigationProps) => {
  const classes     = useStyles({collapsed, mobile});
  const isCollapsed = mobile ? false : collapsed;
  const useFooter   = mobile ? footerItems.length > 0 : true;
  const useHeader   = mobile ? !!mobileHeaderContent : true;

  const [suppressedCurrentSubmenuIndex, setSuppressedCurrentSubmenuIndex] = useState<number | null>(null);

  const {
    closeSubmenuFlyout,
    handleSubmenuToggle,
    registerSubmenuAnchor,
    submenuAnchor,
    submenuAnchorIndex,
    useTopNavigationForActiveSubmenu
  } = useNavigation({
    activeIndex,
    closeSecondaryNavigation,
    collapsed : isCollapsed,
    menuItems,
    onSetPage,
    showSecondaryNavigation
  });

  // a collapsed menu item's flyout closes once the pointer moves on to any
  // other part of the nav - the top container (search, Tracking, Filter, the
  // group chooser) and the footer (Alerts, Help, Profile) included - as it
  // does on moving on to another menu item ...
  const closeFlyoutOnHover = useCallback(function() {
    if (isCollapsed && submenuAnchor) {
      closeSubmenuFlyout();
    }
  }, [closeSubmenuFlyout, isCollapsed, submenuAnchor]);

  const resolvedHeaderContent = useMemo(
    () => {
      const content = mobile ? mobileHeaderContent : headerContent;
      return isFunction(content) ? content(isCollapsed, mobile ? onClose as (() => void) : undefined) : content;
    },
    [headerContent, isCollapsed, mobile, mobileHeaderContent, onClose]
  );

  const resolvedFooterContent = useMemo(
    () => isFunction(footerContent) ? footerContent(isCollapsed) : footerContent,
    [isCollapsed, footerContent]
  );

  const currentPageTopNavigationItem = useMemo(() => (
    findTopNavigationItem(menuItems, currentPage)
  ), [currentPage, menuItems]);

  // the section of the page still on screen while another one loads ...
  const renderedPageTopNavigationItem = useMemo(() => (
    renderedPage === undefined ? undefined : findTopNavigationItem(menuItems, renderedPage)
  ), [menuItems, renderedPage]);

  const currentPageInActiveSubmenu = useMemo(() => (
    isCurrentPageInMenuItem(menuItems[activeIndex], currentPage)
  ), [activeIndex, currentPage, menuItems]);

  const topNavigationItem = useMemo(() => {
    if (useTopNavigationForActiveSubmenu && currentPageInActiveSubmenu) {
      return menuItems[activeIndex];
    }
    return currentPageTopNavigationItem;
  }, [
    menuItems,
    activeIndex,
    currentPageInActiveSubmenu,
    currentPageTopNavigationItem,
    useTopNavigationForActiveSubmenu
  ]);

  // the top navigation follows the page actually on screen: a new section's
  // bar waits until one of its pages has rendered, and the section being
  // left keeps its bar until then - a route change within a section keeps
  // its bar, with the new tab selected straight away ...
  const topNavigationPageRendered = useMemo(() => (
    renderedPage === undefined || isCurrentPageInMenuItem(topNavigationItem, renderedPage)
  ), [renderedPage, topNavigationItem]);

  const displayedTopNavigationItem = topNavigationPageRendered ? topNavigationItem : renderedPageTopNavigationItem;
  const displayedPage              = topNavigationPageRendered ? currentPage : renderedPage;

  const topNavigationParentIndex = useMemo(() => (
    displayedTopNavigationItem ? menuItems.indexOf(displayedTopNavigationItem) : -1
  ), [menuItems, displayedTopNavigationItem]);

  const topNavigationItems = displayedTopNavigationItem?.pages || [];

  const showTopNavigation = !!displayedTopNavigationItem && topNavigationItems.length > 0;

  useEffect(() => {
    setSuppressedCurrentSubmenuIndex(null);
  }, [currentPage]);

  useEffect(() => {
    if (mobile && open) {
      setSuppressedCurrentSubmenuIndex(null);
    }
  }, [mobile, open]);

  useEffect(() => {
    onTopNavigationVisibilityChange?.(showTopNavigation ? TOP_NAVIGATION_OFFSET_HEIGHT : null);
  }, [onTopNavigationVisibilityChange, showTopNavigation]);

  useEffect(() => {
    return () => onTopNavigationVisibilityChange?.(null);
  }, [onTopNavigationVisibilityChange]);

  /* eslint-disable max-params */
  const handleTopNavigationSetPage = useCallback((
    page: string,
    index: number,
    isSecondaryIndex?: boolean,
    item?: MenuItem,
    isMainMenu?: boolean
  ) => {
    onSetPage(
      page,
      topNavigationParentIndex >= 0 ? topNavigationParentIndex : index,
      isSecondaryIndex,
      item,
      isMainMenu
    );
  }, [onSetPage, topNavigationParentIndex]);
  /* eslint-enable max-params */

  return (
    <>
      <NavigationTopBar activeLabel={displayedTopNavigationItem?.label}
        collapsed={isCollapsed}
        currentPage={displayedPage}
        mobile={mobile}
        onSetPage={handleTopNavigationSetPage}
        secondaryMenuItems={topNavigationItems}
        visible={showTopNavigation} />
      <Drawer variant={mobile ? 'temporary' : 'permanent'}
        classes={{
          paper : classnames(drawerPaperClassName, classes.drawerPaperAccent, {
            [classes.mobileDrawerPaper] : mobile
          }),
          paperAnchorDockedLeft : drawerPaperAnchorLeftClassName,
          root                  : classnames(drawerRootClassName, {
            [classes.mobileDrawerRoot] : mobile
          })
        }}
        elevation={mobile ? 0 : undefined}
        BackdropProps={mobile ? {className: classes.mobileBackdrop} : undefined}
        onClose={onClose as any}
        open={open}>
        <div className={classes.shell}>
          <div className={classes.bodyContent}>
            <Render.If condition={useHeader}>
              <div className={classes.headerHoverArea} onMouseEnter={closeFlyoutOnHover}>
                <NavigationHeader collapsed={isCollapsed}
                  mobile={mobile}
                  content={resolvedHeaderContent}
                  onHandleCollapse={onHandleCollapse}
                  useExpandCollapseFeature={useExpandCollapseFeature} />
              </div>
            </Render.If>
            <div className={classes.primarySection}>
              <NavigationList items={menuItems}
                selectedIndex={selectedIndex}
                activeIndex={activeIndex}
                currentPage={currentPage}
                collapsed={isCollapsed}
                mobile={mobile}
                expandedIndex={showSecondaryNavigation && !useTopNavigationForActiveSubmenu ? activeIndex : -1}
                expandedItems={!isCollapsed && !useTopNavigationForActiveSubmenu ? secondaryMenuItems : []}
                showSecondaryNavigation={showSecondaryNavigation}
                hasSubmenuAnchor={!!submenuAnchor}
                submenuAnchor={submenuAnchor}
                submenuAnchorIndex={submenuAnchorIndex}
                suppressedCurrentSubmenuIndex={suppressedCurrentSubmenuIndex}
                registerSubmenuAnchor={registerSubmenuAnchor}
                onCurrentSubmenuClose={setSuppressedCurrentSubmenuIndex}
                onSubmenuToggle={handleSubmenuToggle}
                onSubmenuClose={closeSubmenuFlyout}
                onSetPage={onSetPage} />
            </div>
          </div>
          <Render.If condition={useFooter}>
            <div className={classes.footerSection} onMouseEnter={closeFlyoutOnHover}>
              <NavigationList items={footerItems}
                currentPage={currentPage}
                collapsed={isCollapsed}
                onSetPage={onSetPage}
                selectedIndex={-1}
                mobile={mobile}
                footer />
              <Render.If condition={!mobile}>
                {resolvedFooterContent}
              </Render.If>
            </div>
          </Render.If>
        </div>
      </Drawer>
    </>
  );
};

const MemoizedNavigation = memo(Navigation);

export {MemoizedNavigation as Navigation};
export default MemoizedNavigation;
